/**
 * Detect when the PWA is running inside a Trusted Web Activity (Android app).
 *
 * Google Play policy 3.2 forbids selling digital goods via non-Play payment
 * methods (Stripe, PayPal, ...) inside apps published on the Play Store, so we
 * must hide checkout flows when the current runtime is the TWA.
 *
 * The most reliable signal is the `android-app://` referrer that Chrome injects
 * when it launches a TWA. `display-mode: standalone` alone is not enough
 * because installed PWAs on desktop also match it.
 */

const TWA_PACKAGE = "com.sux1.app";

/** True when the current page was launched by the SUX1 Android app. */
export function isRunningInTWA() {
    if (typeof document === "undefined") return false;
    const ref = document.referrer || "";
    if (ref.startsWith(`android-app://${TWA_PACKAGE}`)) return true;
    // Fallback: some Chrome versions strip the referrer but expose the package
    // through the Digital Goods API — treat that as TWA too.
    if (
        typeof window !== "undefined" &&
        typeof window.getDigitalGoodsService === "function" &&
        window.matchMedia?.("(display-mode: standalone)")?.matches
    ) {
        return /Android/i.test(navigator.userAgent);
    }
    return false;
}

/** Public URL to open externally when the user wants to pay from the TWA. */
export const WEB_UPGRADE_URL = "https://sux1.app/upgrade";

/**
 * Open a URL in the *external* system browser (Chrome) — critical when the
 * caller runs inside our TWA. A plain `window.open(url, "_blank")` still gets
 * re-captured by the TWA because the target host is declared in
 * `assetlinks.json`, trapping the user in an infinite redirect loop.
 *
 * On Android we build an Android `intent://` URL that explicitly targets the
 * Chrome package, which forces the OS to hand the URL to Chrome (outside the
 * TWA) regardless of the digital asset link verification.
 *
 * On any other platform (or when the URL can't be parsed) we fall back to a
 * standard new-tab open.
 */
export function openExternalBrowser(url) {
    if (typeof window === "undefined") return;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isAndroid = /Android/i.test(ua);

    if (isAndroid) {
        try {
            const u = new URL(url);
            // Everything after the scheme://host, keeping path + query + hash.
            const rest = u.pathname + u.search + u.hash;
            const intent =
                `intent://${u.host}${rest}` +
                `#Intent;scheme=${u.protocol.replace(":", "")}` +
                `;package=com.android.chrome` +
                `;S.browser_fallback_url=${encodeURIComponent(url)};end`;
            // Use location.href so Android resolves the intent immediately.
            window.location.href = intent;
            return;
        } catch {
            // fall through to window.open
        }
    }

    window.open(url, "_blank", "noopener,noreferrer");
}
